import React from 'react';
import { Box, LinearProgress, Typography, List, ListItem } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { PasswordStrength } from '../utils/passwordValidator';

interface PasswordStrengthIndicatorProps {
  strength: PasswordStrength;
}

const PasswordStrengthIndicator: React.FC<PasswordStrengthIndicatorProps> = ({ strength }) => {
  const { t } = useTranslation();

  const getColor = (score: number): 'error' | 'warning' | 'success' => {
    if (score < 3) return 'error';
    if (score < 5) return 'warning';
    return 'success';
  };

  const getLabel = (score: number): string => {
    if (score < 3) return t('password.strength.weak');
    if (score < 5) return t('password.strength.medium');
    return t('password.strength.strong');
  };

  return (
    <Box sx={{ width: '100%', mt: 1, mb: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
        <LinearProgress
          variant="determinate"
          value={(strength.score / 5) * 100}
          color={getColor(strength.score)}
          sx={{
            height: 6,
            borderRadius: 3,
            flexGrow: 1,
            backgroundColor: 'rgba(0, 0, 0, 0.08)',
            '& .MuiLinearProgress-bar': {
              borderRadius: 3,
            },
          }}
        />
        <Typography
          variant="caption"
          color={getColor(strength.score)}
          sx={{ ml: 1, fontWeight: 500, minWidth: 60 }}
        >
          {getLabel(strength.score)}
        </Typography>
      </Box>
      {strength.feedback.length > 0 && (
        <List dense disablePadding sx={{ mt: 0.5 }}>
          {strength.feedback.map((feedback, index) => (
            <ListItem 
              key={index} 
              sx={{ 
                py: 0.25,
                px: 0,
                color: 'text.secondary',
                fontSize: '0.75rem',
              }}
            >
              <Typography variant="caption" component="span" sx={{ display: 'flex', alignItems: 'center' }}>
                • {t(`password.feedback.${feedback}`)}
              </Typography>
            </ListItem>
          ))}
        </List>
      )}
    </Box>
  );
};

export default PasswordStrengthIndicator; 